// Types.
import type event from '../Types/Event';
import type source from '../Types/Source';

// Interfaces.
import { Options } from '../Interfaces/Options';

// Methods.
import { toElementOrWindow } from '../Helpers/Helpers';

// Classes.
import Listener from './Listener';
import Observer from './Observer';

/**
 * Scrolly.Handler
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-16 New this.node type.
 * @package fk_js/scrolly
 * @version: 1.2.1
 */
abstract class Handler<HandlerOptions extends Options = Options>
{

    private enabled    :boolean = true;

    protected listener :Listener;
    protected observer :Observer;
    protected options  :HandlerOptions;
    protected node     :Element|Window|null = window;

    /**
     * Constructor.
     */
    constructor(options :HandlerOptions)
    {
        this.listener = Listener.subscribe(this);
        this.observer = Observer.init();
        this.options  = {...(this.defaults() || {}), ...options};
    }

    // ################
    // Abstract methods
    // ################

    /**
     * Define default settings.
     */
    abstract defaults() :HandlerOptions;

    /**
     * Define to which event types this callback listens.
     */
    abstract events() :event[];

    /**
     * Check if any animation frames should be queued or not and return them in an array.
     */
    public requestAnimationFrames(event :Event, handler :Listener) :Function[]
    {
        return [];
    };

    // ##############
    // Public methods
    // ##############

    public appliesTo(node :source) :boolean
    {
        return true;
        //TODO: currently not working ...
        if (document === node) {
            node = window;
        }
        if (!this.node) {
            return false;
        }
        return this.node === node || (this.node.contains && this.node.contains(node));
    }

    public disable() :Handler
    {
        this.enabled = false;
        return this;
    }

    public isDisabled() :boolean
    {
        return !this.enabled;
    }

    public isEnabled() :boolean
    {
        return this.enabled;
    }

    public enable() :Handler
    {
        this.enabled = true;
        return this;
    }

    public listensTo(event :event) :boolean
    {
        return 0 <= this.events().indexOf(event);
    }

    // #######
    // Setters
    // #######

    public setNode(source :source) :Handler
    {
        this.node = toElementOrWindow(source);
        return this;
    }

    // #######
    // Getters
    // #######

    public getNode() :HTMLElement|window
    {
        return this.node;
    }

    // ##############
    // Static methods
    // ##############

    public static init(...args :any[]) :any
    {
        return new (this as any)(...args);
    }

}

export default Handler;